import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, CreditCard } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatMoney, paymentMethodLabel } from "@/lib/format";

const STATUS_STYLE = {
    paid:      "bg-emerald-500/15 border-emerald-400/40 text-emerald-100",
    pending:   "bg-amber-500/15 border-amber-400/40 text-amber-100",
    canceled:  "bg-red-500/10 border-red-400/30 text-red-200",
    partiallypaid: "bg-blue-500/10 border-blue-400/30 text-blue-100",
    refunded:  "bg-blue-500/10 border-blue-400/30 text-blue-100",
};

export function StaffPayments() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const requestVersion = useRef(0);
    const retryTimer = useRef(null);

    const [q, setQ] = useState("");
    const [status, setStatus] = useState(params.get("status") || "all");
    const [method, setMethod] = useState("all");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");

    const load = () => {
        const version = ++requestVersion.current;
        clearTimeout(retryTimer.current);
        setError(null); setData(null);
        const attempt = async (retry = false) => {
            try {
                const result = await staffApi.payments();
                if (version === requestVersion.current) setData(result);
            } catch (e) {
                if (version !== requestVersion.current) return;
                if (!retry && [502,503,504].includes(e.status)) {
                    retryTimer.current = setTimeout(() => attempt(true), 750);
                } else setError(e);
            }
        };
        attempt();
    };
    useEffect(() => {
        load();
        return () => { ++requestVersion.current; clearTimeout(retryTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync status filter -> URL for deep-links
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (status !== "all") next.set("status", status); else next.delete("status");
        setParams(next, { replace: true });
        // eslint-disable-next-line
    }, [status]);

    const payments = data?.payments || [];

    // Amelia's actual gateway list — never invented.
    const methodOptions = useMemo(() => {
        const set = new Set();
        payments.forEach((p) => { if (p.gateway) set.add(p.gateway); });
        return ["all", ...Array.from(set).sort()];
    }, [payments]);

    // Real statuses Amelia sends
    const statusOptions = useMemo(() => {
        const set = new Set();
        payments.forEach((p) => { if (p.status) set.add(p.status); });
        return ["all", "deposit-outstanding", "outstanding", ...Array.from(set).sort()];
    }, [payments]);

    const filtered = useMemo(() => {
        let list = payments;
        if (status === 'deposit-outstanding') {
            list = list.filter(p => p.depositDue === true);
        } else if (status === "outstanding") {
            list = list.filter((p) => p.balanceDue === true);
        } else if (status !== "all") {
            list = list.filter((p) => (p.status || "").toLowerCase() === status.toLowerCase());
        }
        if (method !== "all") list = list.filter((p) => (p.gateway || "") === method);
        if (start) list = list.filter((p) => (p.appointmentDate || p.dateTime || "") >= start);
        if (end) list = list.filter((p) => (p.appointmentDate || p.dateTime || "") <= end + "T23:59:59");
        if (q) {
            const needle = q.toLowerCase();
            list = list.filter((p) => (
                (p.customerName || "").toLowerCase().includes(needle) ||
                (p.customerEmail || "").toLowerCase().includes(needle) ||
                (p.treatment || "").toLowerCase().includes(needle) ||
                String(p.appointmentId || "").includes(needle)
            ));
        }
        // Freshest first (by appointmentDate then dateTime).
        return list.slice().sort((a, b) => {
            const ka = a.appointmentDate || a.dateTime || "";
            const kb = b.appointmentDate || b.dateTime || "";
            return kb.localeCompare(ka);
        });
    }, [payments, status, method, start, end, q]);

    const totals = useMemo(() => {
        const paid = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
        const outstanding = filtered.reduce((s, p) => s + Number((status === 'deposit-outstanding' ? p.depositOutstanding : p.outstandingBalance) || 0), 0);
        return { paid, outstanding, count: filtered.length };
    }, [filtered, status]);

    return (
        <div className="space-y-6" data-testid="staff-payments">
            <header>
                <h1 className="font-serif font-light text-4xl text-white">Payments</h1>
                <p className="text-sm text-white/50 mt-1">
                    View payments, deposits and outstanding balances in one place.
                </p>
            <button type="button" onClick={load} disabled={!data && !error} className="text-gold mt-2 disabled:opacity-50">{!data && !error ? 'Loading payments…' : 'Refresh'}</button>
            </header>

            {error && <ErrorState title="We couldn't load payments." label="Payments unavailable" error={error} onRetry={load} />}
            {!data && !error && <Skeleton className="h-64" />}

            {data && (
                <>
                    {/* Totals for the current filter */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="payment-totals">
                        <div className="surface-highlight rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">Paid (filtered)</div>
                            <div className="mt-2 font-serif text-2xl text-gold">{formatMoney(totals.paid)}</div>
                        </div>
                        <div className="surface-highlight rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">{status === 'deposit-outstanding' ? 'Deposits due' : 'Outstanding (filtered)'}</div>
                            <div className="mt-2 font-serif text-2xl text-gold">{formatMoney(totals.outstanding)}</div>
                        </div>
                        <div className="surface-highlight rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">Records</div>
                            <div className="mt-2 font-serif text-2xl text-gold">{totals.count}</div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-2" data-testid="payment-filters">
                        <div className="relative">
                            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="payments-search"
                                   placeholder="Search by customer, treatment or booking"
                                   className="pl-9 pr-3 h-9 w-72 max-w-full rounded-full bg-transparent border hairline text-sm text-white placeholder-white/40 focus:border-gold outline-none" />
                        </div>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
                                className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">
                            {statusOptions.map((s) => <option key={s} value={s} className="bg-navy">{s === "all" ? "All statuses" : s === "deposit-outstanding" ? "Outstanding deposits" : s === "outstanding" ? "Outstanding payments" : s === "partiallyPaid" ? "Part paid" : s}</option>)}
                        </select>
                        <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="filter-method"
                                className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">
                            {methodOptions.map((m) => <option key={m} value={m} className="bg-navy">{m === "all" ? "All methods" : paymentMethodLabel(m, "paid")}</option>)}
                        </select>
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="filter-start" aria-label="From date"
                               className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3" />
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="filter-end" aria-label="To date"
                               className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3" />
                    </div>

                    {filtered.length === 0 ? (
                        <div className="rounded-2xl glass p-8 text-center text-white/60" data-testid="payments-empty">
                            {q || method !== 'all' || start || end ? 'No payment records match these filters.' : status === 'outstanding' ? 'No outstanding payments.' : status === 'deposit-outstanding' ? 'No outstanding deposits.' : payments.length === 0 ? 'No payment records yet.' : 'No payment records match these filters.'}
                        </div>
                    ) : (
                        <ul className="space-y-2" data-testid="payment-list">
                            {filtered.map((p) => {
                                const status = (p.status || "").toLowerCase();
                                const outstanding = Number(p.outstandingBalance || 0);
                                const paidClass = STATUS_STYLE[status] || "bg-white/10 border-white/25 text-white/80";
                                return (
                                    <li key={p.id} className="payment-card surface-highlight rounded-2xl border hairline p-4 hover:border-gold/50" data-testid={`payment-${p.id}`}>
                                        <Link to={p.appointmentId ? `/staff/appointments/${p.appointmentId}` : "#"} className="payment-card-layout">
                                            <div className="h-10 w-10 rounded-full glass flex items-center justify-center shrink-0">
                                                <CreditCard className="h-4 w-4 text-gold" strokeWidth={1.5} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-white font-ui text-base break-words">{p.customerName || "—"}</div>
                                                <div className="text-sm text-white/75 break-words">{p.treatment || "—"}</div>
                                                <div className="text-xs text-white/70 flex flex-wrap gap-2 mt-2">
                                                    {p.appointmentDate && <span>{formatUKDateTime(p.appointmentDate)}</span>}
                                                    {p.employeeId && <><span>·</span><span className="normal-case tracking-normal">Employee #{p.employeeId}</span></>}
                                                </div>
                                            </div>
                                            <div className="payment-card-amounts">
                                                <div className="text-white font-ui">{formatMoney(p.amount || 0)} <span className="text-white/40">paid</span></div>
                                                <div className={`text-[11px] ${outstanding > 0 ? "text-amber-200" : "text-emerald-200"}`}>
                                                    {formatMoney(outstanding)} balance owing
                                                    <div>{p.depositOutstanding == null ? 'Deposit settings unavailable' : `${formatMoney(p.depositOutstanding)} deposit due`}</div>
                                                </div>
                                            </div>
                                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/70" data-testid={`payment-method-${p.id}`}>
                                                {p.gatewayTitle || paymentMethodLabel(p.gateway, p.status)}
                                            </span>
                                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${paidClass}`} data-testid={`payment-status-${p.id}`}>
                                                {status === "partiallypaid" ? "Part paid" : p.status || "Unknown"}
                                            </span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}

function formatUKDateTime(iso) {
    if (!iso) return "";
    try { return new Date(iso.replace(" ", "T")).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
}
